/* English — base translation. Every key used in the app must exist here. */
const en = {
  /* ————— Navbar ————— */
  nav: {
    home: 'Home',
    imageConverter: 'Image Converter',
    imageCompressor: 'Image Compressor',
    otherTools: 'Other Tools',
    resizeImage: 'Resize Image',
    cropImage: 'Crop Image',
    photoEditor: 'Photo Editor',
    removeBackground: 'Remove Background',
    watermarkImage: 'Watermark Image',
    qrCodeGenerator: 'QR Code Generator',
    qrCodeScanner: 'QR Code Scanner',
    aboutUs: 'About Us',
    faceBlur: 'Face Blur',
    blogs: 'Blogs',
  },

  /* ————— Hero / Home ————— */
  hero: {
    heading: 'Every Image Tool You Need',
    subheading: 'Free online tools to convert, compress, resize, crop, edit and transform your images. All processing happens in your browser — fast, private & secure.',
  },

  homePaste: {
    badge: 'Smart Paste',
    title: 'Pasted content detected',
    subtitle: 'Choose a tool and continue instantly with your pasted images, links, or text.',
    imagesLabel: 'Image Preview',
    textLabel: 'Detected Text / Link',
    toolsTitle: 'Suggested Tools',
    multiHint: 'Multiple images detected. Tools below support multi-image workflows.',
  },

  /* ————— Tool cards on Home ————— */
  tools: {
    compressImage: { title: 'Compress IMAGE', desc: 'Compress JPG, PNG, SVG, and GIFs while saving space and maintaining quality.' },
    resizeImage: { title: 'Resize IMAGE', desc: 'Define your dimensions, by percent or pixel, and resize your JPG, PNG, SVG and GIF images.' },
    cropImage: { title: 'Crop IMAGE', desc: 'Crop JPG, PNG, or GIFs with ease; choose pixels to define your rectangle or use our visual editor.' },
    convertToJpg: { title: 'Convert to JPG', desc: 'Turn PNG, GIF, TIF, PSD, SVG, WEBP, HEIC, or RAW format images to JPG in bulk with ease.' },
    convertFromJpg: { title: 'Convert from JPG', desc: 'Turn JPG images to PNG and GIF. Choose several JPGs to create an animated GIF in seconds!' },
    qrCodeGenerator: { title: 'QR Code Generator', desc: 'Generate custom QR codes for URLs, text, WiFi, contacts, email & maps. Customize styles, colors, and logos.' },
    qrCodeScanner: { title: 'QR Code Scanner', desc: 'Scan QR codes instantly using your camera or by uploading an image. Detect URLs, text, WiFi, contacts & more.' },
    blurFace: { title: 'Blur Face', desc: 'Automatically detect and blur faces in your images for privacy. Adjust blur intensity, shape, or add emoji overlays.' },
    removeBackground: { title: 'Remove Background', desc: 'Quickly remove image backgrounds with high accuracy. Instantly detect objects and cut out backgrounds.' },
    removeBackgroundAI: { title: 'Remove Background (AI)', desc: 'High-accuracy background removal using cloud AI processing with beautiful edge detection.' },
    watermarkImage: { title: 'Watermark IMAGE', desc: 'Stamp an image or text over your images in seconds. Choose the typography, transparency and position.' },
  },

  /* ————— Why Choose Us ————— */
  whyChoose: {
    label: 'Why Choose Us',
    heading: 'The Smarter Way to Edit Images',
    subheading: 'Trusted by thousands of creators, designers, and developers worldwide. Here\'s why photremium.com is the go-to destination for image editing.',
    reasons: {
      fast: { title: 'Lightning Fast', desc: 'All processing happens directly in your browser. No file uploads to external servers — results appear in milliseconds.' },
      private: { title: '100% Private & Secure', desc: 'Your files never leave your device. We don\'t store, share, or access your images. Complete privacy guaranteed.' },
      noLimits: { title: 'No Limits, No Signup', desc: 'Use every tool as many times as you want — completely free. No account required, no watermarks, no hidden fees.' },
      quality: { title: 'Professional Quality', desc: 'Our algorithms deliver studio-grade results. Compress without visible quality loss, convert with perfect fidelity.' },
      batch: { title: 'Batch Processing', desc: 'Handle multiple files at once. Convert, compress, or resize entire folders of images in a single operation.' },
      everywhere: { title: 'Works Everywhere', desc: 'Fully responsive design works on desktop, tablet, and mobile. No downloads or installations needed.' },
    },
    stats: {
      imagesProcessed: 'Images Processed',
      countriesServed: 'Countries Served',
      uptime: 'Uptime',
      freeTools: 'Free Tools',
    },
  },

  /* ————— Footer ————— */
  footer: {
    tagline: 'Free online image tools — convert, compress, resize, crop & edit your images in seconds. No signup required.',
    imageTools: 'Image Tools',
    moreTools: 'More Tools',
    company: 'Company',
    contact: 'Contact',
    allRightsReserved: 'All rights reserved.',
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
  },

  /* ————— FAQ ————— */
  faq: {
    badge: 'FAQ',
    heading: 'Frequently Asked Questions',
    subheading: 'Quick answers to common questions about this tool.',
    stillHaveQuestions: 'Still have questions? We\'re here to help!',
    reachOut: 'Reach Out to Our Team',
    questionHeader: 'Question',
  },

  /* ————— Contact form ————— */
  contactForm: {
    badge: 'Get in Touch',
    title: 'Contact Us',
    subtitle: "We'd love to hear from you. Drop us a message!",
    nameLabel: 'Name',
    namePlaceholder: 'Your full name',
    emailLabel: 'Email',
    emailPlaceholder: 'you@gmail.com',
    subjectLabel: 'Subject',
    subjectPlaceholder: "What's this about?",
    messageLabel: 'Message',
    messagePlaceholder: 'Write your message here…',
    errName: 'Name is required',
    errEmail: 'Email is required',
    errEmailInvalid: 'Enter a valid email',
    errSubject: 'Subject is required',
    errMessage: 'Message is required',
    sending: 'Sending…',
    send: 'Send Message',
    successTitle: 'Message Sent!',
    successMsg: "Thanks for reaching out. We'll get back to you soon.",
    sendAnother: 'Send Another',
    sendError: 'Something went wrong. Please try again.',
  },

  /* ————— About page ————— */
  about: {
    label: 'About Us',
    heading: 'Built for Creators, by Creators',
    subheading: 'We believe powerful image tools should be free, fast, and accessible to everyone.',
    missionTitle: 'Our Mission',
    missionText: 'photremium.com was created with a simple goal: to provide the most intuitive, fastest, and most private image editing experience on the web. We process everything directly in your browser — your files never leave your device. No accounts, no limits, no compromises.',
    privacyTitle: 'Privacy First',
    privacyText: 'We never upload, store, or process your images on remote servers. Everything stays on your device.',
    techTitle: 'Modern Technology',
    techText: 'Built with cutting-edge web technologies including WebAssembly, Canvas API, and modern React for peak performance.',
    communityTitle: 'Community Driven',
    communityText: 'Every feature we build is inspired by real user feedback. Your suggestions shape our roadmap.',
    globalTitle: 'Global Reach',
    globalText: 'Used by creators in over 120 countries. Available in multiple languages with lightning-fast CDN delivery.',
  },

  /* ————— SEO (meta titles & descriptions) ————— */
  seo: {
    homeTitle: 'Photremium — Free Online Image Converter, Compressor & Editor',
    homeDesc: 'Convert, compress, resize, crop and edit images online for free. Fast, private and secure — all processing happens in your browser.',
    homeKeywords: 'image converter, image compressor, resize image, crop image, photo editor, online image tools, free image tools',
    aboutTitle: 'About Photremium — Our Mission & Story',
    aboutDesc: 'Learn about Photremium, the free online image toolkit trusted by millions. Our mission is to make professional image editing accessible to everyone.',
    aboutKeywords: 'about photremium.com, image tools company, free image editor, about us',
  },

  privacyPolicyPage: {
    seo: {
      title: 'Privacy Policy — Photremium | No Server Uploads, Zero Data Storage',
      description: "Photremium's Privacy Policy explains how we protect your privacy. All image processing happens 100% in your browser — no file uploads to any server, no personal data collected, no tracking.",
      ogTitle: 'Privacy Policy — Photremium | No Uploads, Zero Storage',
      ogDescription: 'Your files stay on your device. Photremium processes all images locally in your browser with no server contact, no data stored, and no tracking.',
      twitterTitle: 'Privacy Policy — Photremium',
      twitterDescription: '100% browser-based image tools. No uploads, no storage, no tracking. Read our full privacy policy.',
      schemaName: 'Privacy Policy - Photremium',
      schemaDescription: 'Photremium Privacy Policy - no server uploads, no data stored, 100% in-browser image processing.',
    },
    hero: {
      badge: 'Privacy Policy',
      titlePrefix: 'Your Privacy Is Our',
      titleHighlight: 'Top Priority',
      subPrefix: 'photremium.com is built on a simple principle —',
      subStrong: 'your files never leave your device',
      subSuffix: 'Every image tool on this site runs entirely inside your browser. No uploads. No cloud. No compromise.',
      effectiveLabel: 'Effective',
      lastUpdatedLabel: 'Last updated',
      worldwideLabel: 'Applies worldwide',
    },
    trustCards: [
      { title: 'No Server Uploads', desc: 'Your files never leave your device. All processing is 100% in-browser.' },
      { title: 'Zero Data Storage', desc: 'We store nothing about you, your images, or your activity.' },
      { title: 'No Tracking', desc: 'No behavioral tracking, no fingerprinting, no profiling.' },
      { title: 'GDPR Friendly', desc: 'Designed from the ground up to respect global privacy regulations.' },
    ],
    contentsTitle: 'Contents',
    toc: {
      overview: 'Overview',
      'no-upload': 'No Server Processing',
      'data-collect': 'Data We Collect',
      cookies: 'Cookies & Storage',
      'third-party': 'Third-Party Services',
      analytics: 'Analytics',
      children: "Children's Privacy",
      rights: 'Your Rights',
      security: 'Security',
      changes: 'Policy Changes',
      contact: 'Contact Us',
    },
    sections: {
      overview: {
        title: '1. Overview',
        subtitle: 'What this policy covers',
        p1Prefix: 'This Privacy Policy describes how',
        p1Middle: '("we", "us", or "our") handles - or more accurately,',
        p1Italic: "doesn't handle",
        p1Suffix: 'your personal information when you use our free online image tools at',
        p2: 'Unlike traditional web applications that send files to remote servers for processing, photremium.com uses modern browser APIs (Canvas API, WebAssembly, WebWorkers) to perform every operation locally on your device. The result: no data transmission, no storage, no risk.',
        calloutLabel: 'Plain English summary:',
        calloutText: "We can't see your images, we don't want to, and our technology is specifically engineered so that we never could.",
      },
      noUpload: { title: '2. No Server Processing — Ever', subtitle: 'How our tools actually work' },
      dataCollect: { title: "3. Data We Collect (and Don't Collect)", subtitle: 'Full transparency on data practices' },
      cookies: { title: '4. Cookies & Local Storage', subtitle: 'What gets stored on your device' },
      thirdParty: { title: '5. Third-Party Services', subtitle: 'External services we use and why' },
      analytics: { title: '6. Analytics', subtitle: 'Aggregate, privacy-respecting usage data' },
      children: { title: "7. Children's Privacy", subtitle: 'COPPA & child safety compliance' },
      rights: { title: '8. Your Rights', subtitle: 'GDPR, CCPA, and global privacy rights' },
      security: { title: '9. Security', subtitle: 'How we protect your experience' },
      changes: { title: '10. Changes to This Policy', subtitle: 'How we handle policy updates' },
      contact: { title: '11. Contact & Data Requests', subtitle: 'Get in touch about privacy' },
    },
    footer: {
      privacyQuestionsTitle: 'Privacy Questions?',
      privacyQuestionsDesc: "Send us your privacy questions, data requests, or concerns and we'll respond promptly.",
      versionHistoryTitle: 'Version History',
      v2Label: 'v2.0 — Major rewrite',
      v11Label: 'v1.1 — Added CCPA rights',
      v10Label: 'v1.0 — Initial policy',
    },
  },

  /* ————— Common / shared UI strings ————— */
  common: {
    new: 'New!',
    download: 'Download',
    downloadAll: 'Download All',
    downloadAs: 'Download as:',
    zip: 'ZIP',
    separate: 'Separate',
    upload: 'Upload',
    dragDrop: 'Drag & drop images here, or click to browse',
    remove: 'Remove',
    reset: 'Reset',
    apply: 'Apply',
    cancel: 'Cancel',
    clear: 'Clear',
    close: 'Close',
    save: 'Save',
    processing: 'Processing...',
    noFiles: 'No files selected',
    selectFiles: 'Select files',
    or: 'or',
    browseFiles: 'browse files',
    dropHere: 'Drop your images here',
    chooseFiles: 'Choose Files',
    pasteHint: 'You can also paste images with',
    quality: 'Quality',
    width: 'Width',
    height: 'Height',
    format: 'Format',
    compression: 'Compression',
    preview: 'Preview',
    original: 'Original',
    result: 'Result',
    before: 'Before',
    after: 'After',
    startOver: 'Start Over',
    startOverConfirm: 'Are you sure you want to remove all images and start over?',
    addMoreImages: 'Add More Images',
    addImage: 'Add Image',
    images: 'Images',
    totalSize: 'Total Size',
    size: 'Size',
    type: 'Type',
    unsavedEdits: 'You have unsaved edits. Leave this page?',
    compareSlider: 'Compare slider',
    toggleToolsPanel: 'Toggle tools panel',
    closePanel: 'Close panel',
    zoomIn: 'Zoom In',
    zoomOut: 'Zoom Out',
    fit: 'Fit',
    none: 'None',
  },

  /* ————— Image Compressor ————— */
  compressor: {
    seo: {
      uploadTitle: 'Image Compressor — Compress JPG, PNG, SVG, GIF Online Free | Photremium',
      uploadDesc: 'Compress JPG, PNG, SVG and GIF images online for free. Reduce file size by up to 80% while maintaining visual quality. No signup required.',
      uploadKeywords: 'image compressor, compress jpg, compress png, reduce image size, image optimization, compress photos online free',
      workspaceTitle: 'Compressing Images — Photremium Image Compressor',
      workspaceDesc: 'Adjust compression level and download your optimized images. Real-time preview and size estimation.',
      workspaceKeywords: 'compress images, image compression slider, bulk compress images, image optimization tool',
    },
    title: 'Image Compressor',
    desc: 'Compress JPG, PNG & GIF images up to 80% smaller. Fast, private — runs entirely in your browser.',
    toCompress: 'to compress',
    compressionForAll: 'Compression for all images',
    summary: 'Summary',
    originalSize: 'Original Size',
    compressed: 'Compressed',
    saved: 'Saved',
    compressionLabel: 'Compression:',
    compressing: 'Compressing…',
    compressDownload: 'Compress & Download',
    lowQualityWarning: 'High compression may noticeably reduce image clarity.',
    blog: {
      tocTitle: 'Contents',
      title: 'Image Compressor Blog',
      formatsIntro: 'Our tool supports all the image formats. So, you can compress any image seamlessly. List of all the formats is given below:',
      sections: [
        {
          id: 'imgcomp-card-1',
          title: '1. What is an image compressor?',
          paragraphs: [
            'An image compressor is a tool-software or online service that reduces the file size of image while maintaining its visual quality. However, compressing the image too much will significantly reduce its quality which can be visibly noticed.',
          ],
          listTitle: 'TYPES:',
          bullets: [
            'Lossy Compression: In this type of compression, the image size is reduced by removing some image data which reduces its quality but the reduction is not noticeable with the human eye.',
            'Lossless Compression: It is just reducing the image size without affecting the visual quality of the image at all.',
          ],
        },
        {
          id: 'imgcomp-card-2',
          title: '2. Why choose our tool?',
          bullets: [
            'Our AI powered image compressor tool works seamlessly on all devices to serve you the best possible results.',
            'You can compress multiple images (even 100+ images) at a time just by a single click.',
            'No need to upload your images to our servers. All the images will be compressed on your local browser.',
            'Our image compressor tool is completely free forever. You can also share this tool with your friends.',
            'You can select the compression percentage according to your preference.',
          ],
        },
        {
          id: 'imgcomp-card-3',
          title: '3. Step by step guide',
          steps: [
            {
              heading: '1. Select the image/s you want to compress.',
              bullets: [
                'Drag and drop the image/s to the drop box.',
                'You can also copy the image/s and paste in the box via (Ctrl + V)',
                'File explorer is also supported by clicking the choose button.',
              ],
            },
            {
              heading: '2. Then select compression percentage.',
              bullets: [
                'A horizontal slide bar offers you a comfortable experience for selecting the compression percentage. Follow these ranges to get the accurate results.',
              ],
              table: {
                headers: ['Compression Percentage', 'Results'],
                rows: [
                  ['1%  to  40%', 'Best quality'],
                  ['41%  to  60%', 'Normal quality(Recommended)'],
                  ['61%  to  100%', 'Bad quality'],
                ],
              },
            },
            {
              heading: '3. Inspect the memory you saved.',
              paragraphs: [
                'All the memory metrices are displayed at runtime. You can clearly see how much memory you saved out of total. So, you can adjust compression level to get the file size according to your requirement and preference.',
              ],
            },
            {
              heading: '4. Select the download option from toggle button.',
              bullets: [
                'ZIP: If you are compressing multiple images, you will see this option. Selecting ZIP option will archive all your compressed images in a zip file (Recommended for large number of images).',
                'Separate: Selecting this option will download all your images separately NOT as a single ZIP file. (Recommended just for a smaller number of images; less than 10)',
              ],
            },
            {
              heading: '5. Compress & download the image.',
              paragraphs: [
                'This button will download all the compressed images automatically in your selected download option.',
              ],
            },
          ],
        },
        {
          id: 'imgcomp-card-4',
          title: '4. Benifits of image compressor.',
          bullets: [
            'Minimizes storage cost: Compressing the images will significantly reduce their storage costs as their size is now reduced.',
            'Faster loading: Compressed images load faster which improves the user experience for the website or app.',
            'Gmail limitation solution: As Gmail limits the users to attach files up to 25MB only, there is a need for image compression. Instead of attaching smaller number of larger files, compress the images to attach more files.',
            'Faster Sharing and Transfer: Larger files can take more time to share or transfer. So, compressing the images will save the transfer time and data used to transfer those files. Also, some platforms restrict the users to share large files.',
            'Presentation Optimization: A normal presentation with 20 uncompressed images takes up to 100MB file size causing too much lag. However, the similar presentation with same images compressed consists of around 10 to 15MB. Also, large files are hard to upload on academic or job portals.',
          ],
        },
        {
          id: 'imgcomp-card-5',
          title: '5. Supported Formats.',
          formats: ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.avif', '.heic', '.heif', '.jxl', '.bmp', '.ico', '.tiff', '.tif', '.jfif', '.jpe', '.pjpeg', '.pjp', '.apng', '.svgz', '.xbm'],
        },
      ],
    },
  },

  /* ————— Image Converter ————— */
  converter: {
    seo: {
      uploadTitle: 'Image Converter — Convert PNG, JPG, WEBP, GIF, SVG Online Free | Photremium',
      uploadDesc: 'Convert images between JPG, PNG, WEBP, GIF, SVG, TIFF, BMP and more. Free online image format converter — fast, private, no signup required.',
      uploadKeywords: 'image converter, convert png to jpg, jpg to png, webp converter, image format converter, free image converter online',
      workspaceKeywords: 'convert images, image format converter, bulk convert images',

      routeKeywords: {

        'png-to-jpg': 'png to convert jpg, png to jpg, image converter, convert png to jpg, jpg to png, webp converter, image format converter, free image converter online',

        'jpg-to-png': 'jpg to convert png, jpg to png, image converter, convert png to jpg, jpg to png, webp converter, image format converter, free image converter online',

        'png-to-webp': 'png to convert webp, png to webp, image converter, convert png to jpg, jpg to png, webp converter, image format converter, free image converter online',

        'jpg-to-webp': 'jpg to convert webp, jpg to webp, image converter, convert png to jpg, jpg to png, webp converter, image format converter, free image converter online',

        'webp-to-jpg': 'webp to convert jpg, webp to jpg, image converter, convert png to jpg, jpg to png, webp converter, image format converter, free image converter online',

        'webp-to-png': 'webp to convert png, webp to png, image converter, convert png to jpg, jpg to png, webp converter, image format converter, free image converter online',

        'gif-to-jpg': 'gif to convert jpg, gif to jpg, image converter, convert png to jpg, jpg to png, webp converter, image format converter, free image converter online',

        'gif-to-png': 'gif to convert png, gif to png, image converter, convert png to jpg, jpg to png, webp converter, image format converter, free image converter online',

        'bmp-to-jpg': 'bmp to convert jpg, bmp to jpg, image converter, convert png to jpg, jpg to png, webp converter, image format converter, free image converter online',

        'bmp-to-png': 'bmp to convert png, bmp to png, image converter, convert png to jpg, jpg to png, webp converter, image format converter, free image converter online',

        'svg-to-png': 'svg to convert png, svg to png, image converter, convert png to jpg, jpg to png, webp converter, image format converter, free image converter online',

        'svg-to-jpg': 'svg to convert jpg, svg to jpg, image converter, convert png to jpg, jpg to png, webp converter, image format converter, free image converter online',

        'tiff-to-jpg': 'tiff to convert jpg, tiff to jpg, image converter, convert png to jpg, jpg to png, webp converter, image format converter, free image converter online',

        'tiff-to-png': 'tiff to convert png, tiff to png, image converter, convert png to jpg, jpg to png, webp converter, image format converter, free image converter online',

        'jpg-to-gif': 'jpg to convert gif, jpg to gif, image converter, convert png to jpg, jpg to png, webp converter, image format converter, free image converter online',

        'png-to-gif': 'png to convert gif, png to gif, image converter, convert png to jpg, jpg to png, webp converter, image format converter, free image converter online',

        'jpg-to-bmp': 'jpg to convert bmp, jpg to bmp, image converter, convert png to jpg, jpg to png, webp converter, image format converter, free image converter online',

        'png-to-bmp': 'png to convert bmp, png to bmp, image converter, convert png to jpg, jpg to png, webp converter, image format converter, free image converter online',

        'webp-to-gif': 'webp to convert gif, webp to gif, image converter, convert png to jpg, jpg to png, webp converter, image format converter, free image converter online',

        'jpg-to-ico': 'jpg to convert ico, jpg to ico, image converter, convert png to jpg, jpg to png, webp converter, image format converter, free image converter online',

        'png-to-ico': 'png to convert ico, png to ico, image converter, convert png to jpg, jpg to png, webp converter, image format converter, free image converter online',

        'jpg-to-svg': 'jpg to convert svg, jpg to svg, image converter, convert png to jpg, jpg to png, webp converter, image format converter, free image converter online',

        'png-to-svg': 'png to convert svg, png to svg, image converter, convert png to jpg, jpg to png, webp converter, image format converter, free image converter online',

        'webp-to-svg': 'webp to convert svg, webp to svg, image converter, convert png to jpg, jpg to png, webp converter, image format converter, free image converter online',

        'gif-to-svg': 'gif to convert svg, gif to svg, image converter, convert png to jpg, jpg to png, webp converter, image format converter, free image converter online',

        'bmp-to-svg': 'bmp to convert svg, bmp to svg, image converter, convert png to jpg, jpg to png, webp converter, image format converter, free image converter online',

        'tiff-to-svg': 'tiff to convert svg, tiff to svg, image converter, convert png to jpg, jpg to png, webp converter, image format converter, free image converter online',

      },
    },
    title: 'Image Converter',
    desc: 'Convert images between JPG, PNG, WEBP, GIF, SVG, TIFF, BMP and more. Batch convert multiple files at once — fast, private, runs entirely in your browser.',
    toConvert: 'to convert',
    convertSettings: 'Convert Settings',
    detected: 'Detected',
    convertTo: 'Convert to',
    converting: 'Converting…',
    convertToFormat: 'Convert to {format}',
    popularConversions: 'Popular Conversions',
    convertingTo: 'Converting to',
    all: 'All',
  },

  /* ————— Resize Image ————— */
  resizer: {
    seo: {
      uploadTitle: 'Resize Image Online — Resize JPG, PNG, SVG, GIF Free | Photremium',
      uploadDesc: 'Resize images online for free by pixel or percentage. Resize JPG, PNG, SVG and GIF images while preserving quality. No signup required.',
      uploadKeywords: 'resize image, image resizer, resize jpg, resize png, change image dimensions, resize photo online free',
      workspaceTitle: 'Resizing Images — Photremium Image Resizer',
      workspaceDesc: 'Resize images online for free by pixel or percentage. No signup required.',
      workspaceKeywords: 'resize image, image resizer, resize jpg, resize png',
    },
    title: 'Resize Image',
    desc: 'Define your dimensions by pixel, percentage, centimeters or inches and resize your JPG, PNG, SVG and GIF images instantly. Batch resize multiple files at once.',
    toResize: 'to resize',
    resizeSettings: 'Resize Settings',
    resizeBy: 'Resize by:',
    byPixels: 'By Pixels',
    byPercentage: 'By Percentage',
    byCentimeters: 'By Centimeters',
    byInches: 'By Inches',
    advanced: 'Advanced',
    lockAspect: 'Lock aspect ratio',
    unlockAspect: 'Unlock aspect ratio',
    dontEnlarge: "Don't enlarge if original is smaller",
    resizing: 'Resizing…',
    resizeDownload: 'Resize & Download',
    blog: {
      tocTitle: 'Contents',
      title: 'Image Resizer Blog',
      sections: [
        {
          id: 'imgresize-card-1',
          title: '1. Why use image resizer?',
          paragraphs: [
            'An image resizer is a tool or software used to change the dimensions of image. It allows you to adjust the size of the desired image by changing either its height, width, or both. This tool is especially useful for social media platforms which have restriction of the size to upload or post images.',
          ],
          listTitle: 'For Example:',
          bullets: [
            'Instagram posts: 1080 x 1080px(Square)',
            'Facebook post: 1200 x 630px',
            'X (Former Twitter) post: 1200 x 675px',
            'Instagram Story: 1080 x 1920px',
          ],
        },
        {
          id: 'imgresize-card-2',
          title: '2. Why choose our Image Resizer tool?',
          paragraphs: [
            'Our AI powered image resizer makes the image resizing process accurate and extremely fast. You can navigate this tool on each device seamlessly. There is no need to install or download any app. All services are available online in the browser of your choice.',
          ],
          bullets: [
            'You can resize multiple images (even 100+) at once with our resize-image tool. If you are having a high-performance device, you can conveniently resize 400 to 500 images within seconds.',
            'All the processing will be done on your local browser. You don’t have to upload any of the images to over servers. Your images will never leave your device. So, you can resize your personal images without any privacy related risk.',
            'Multiple units are supported for resizing the image.',
            'You can also maintain the original aspect ratio of the image even after resizing it, just by selecting the checkbox.',
            'If you are resizing multiple images, you have a choice to download the resized images as separate ones or as a ZIP file.',
          ],
        },
        {
          id: 'imgresize-card-3',
          title: '3. Step by step guide',
          steps: [
            {
              heading: 'First of all, select the image or multiple images you want to resize. You have multiple ways to select the image.',
              bullets: [
                'You can just drag and drop image/s to resize them.',
                '“Ctrl + V” shortcut is also supported to paste the image in the drop box.',
                'Clicking the “Choose Files” button will lead to the file explorer. You can also select your desirable images for resizing.',
              ],
            },
            {
              heading: 'Then, you have an option to select one of the units to resize the image. Currently, we offer these units:',
              bullets: ['Pixels', 'Percentage', 'Centimeters', 'Inches'],
            },
            {
              heading: 'After unit selection, adjust height and with to resize the image. You can also lock the aspect ratio by ticking the checkbox.',
            },
            {
              heading: 'Then tick the “Don’t enlarge if original is smaller” checkbox if you want the resized image not to enlarge from its original size.',
            },
            {
              heading: 'After it, select the download option.',
              bullets: [
                'Select ZIP if you want to resize multiple images and want to download all of them as a zip file.',
                'Or select “separate” to download each resized image separately.',
              ],
            },
            {
              heading: 'Then tap the “Resize & Download” button to download your resized images according to your selected download option.',
            },
          ],
        },
        {
          id: 'imgresize-card-4',
          title: '4. Benefits of image resizing tool: The most notable benefit of this tool is in the proper placement of images inside webpages and social media posts.',
          tocLabel: '4. Benefits of image resizing tool',
          bullets: [
            'Boosting UX: Right size and proper placement of images enhance the UX greatly. Placing too large or too small images without proper adjustment leads to a bad user experience.',
            'Social Media Support: Several social media apps have their own dimension limits for posting images. Our image resizer tool helps you resize your images according to those requisite dimensions.',
            'Responsive Design: If you are a developer and trying to create a responsive design, you must resize the images in multiple dimensions to support multiple devices.',
            'Best Choice: Instead of stretching or cropping the images which can result in changing their dimensions, image resizer is a best option.',
          ],
        },
        {
          id: 'imgresize-card-5',
          title: '5. Supported Formats: Our image resizer tool allows you to select image of any format to be resized. Here are the formats:',
          tocLabel: '5. Supported Formats',
          formats: ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.avif', '.heic', '.heif', '.jxl', '.bmp', '.ico', '.tiff', '.tif', '.jfif', '.jpe', '.pjpeg', '.pjp', '.apng', '.svgz', '.xbm'],
        },
      ],
    },
  },

  /* ————— Crop Image ————— */
  cropper: {
    seo: {
      uploadTitle: 'Crop Image Online — Crop JPG, PNG, GIF Free | Photremium',
      uploadDesc: 'Crop JPG, PNG and GIF images online with ease. Choose pixels to define your crop area or use our visual editor. Free, fast and private.',
      uploadKeywords: 'crop image, image cropper, crop jpg, crop png, crop photo online free, image crop tool',
      workspaceTitle: 'Cropping Images — Photremium Image Cropper',
      workspaceDesc: 'Crop images online for free. Visual crop editor with preset ratios. No signup required.',
      workspaceKeywords: 'crop image, image cropper, crop jpg, crop png',
    },
    title: 'Crop Image',
    desc: 'Crop JPG, PNG, GIF & more with our visual editor. Choose from preset ratios or free-form crop. Runs entirely in your browser.',
    toCrop: 'to crop',
    cropSettings: 'Crop Settings',
    widthPx: 'Width (px)',
    heightPx: 'Height (px)',
    positionX: 'Position X (px)',
    positionY: 'Position Y (px)',
    cropTemplate: 'Crop Template',
    landscape: 'Landscape',
    portrait: 'Portrait',
    square: 'Square',
    freeform: 'Freeform',
    cropping: 'Cropping…',
    cropDownload: 'Crop & Download',
    cropDownloadAll: 'Crop & Download All',
    saveNext: 'Save & Next',
    croppedNofTotal: 'You have cropped {n} out of {total} images.',
    downloadN: 'Download {n}',
    blog: {
      tocTitle: 'Contents',
      title: 'Image Cropper Blog',
      sections: [
        {
          id: 'imgcrop-card-1',
          title: '1. Why use image cropper?',
          paragraphs: [
            'Image cropper is a software tool that is used to cut the image in desired shape. It is widely used to cut a required piece of image and remove unwanted outer part for any purpose. It provides a crop box which you can adjust in the position in which you want to crop.',
          ],
          listTitle: 'For Example:',
          bullets: [
            'Cutting out your own image from a group photo.',
            'Cropping your name from a list.',
            'Cropping the progress bar from the full screen snippet.',
          ],
        },
        {
          id: 'imgcrop-card-2',
          title: '2. Why choose our image cropper?',
          paragraphs: [
            'Our image cropper tool allows you to crop images accurately and really fast. You just have to adjust size of crop box in the position you want to cut out from any image. Almost all image formats are supported for the selection of the image.',
          ],
          bullets: [
            'You can crop as much number of images as your device supports. If you are having a best-performance device, you can crop even 100+ images at once without any lag or other issues.',
            'Your images will be processed on your local browser. No need to upload any image to our servers, so no image leaves your device. You can crop your personal images without any insecurity or risk.',
            'Many tools flag error of invalid image format. Our image cropper tool takes care of this issue by supporting almost all the image formats.',
            'If you are cropping multiple images at once, our tool allows you to download the cropped images as a ZIP or as separate images according to your preference.',
          ],
        },
        {
          id: 'imgcrop-card-3',
          title: '3. Step by step guide',
          steps: [
            {
              heading: '1. First of all, select the image you want to crop. Selecting multiple images at once is also supported by our image cropper tool.',
              bullets: [
                'Drag and drop the image in the drop box to crop it.',
                'You can also select the image via file explorer by clicking the choose files button placed in the crop box.',
                'Ctrl + V shortcut is also supported to paste the images in the drop box.',
              ],
            },
            {
              heading: '2. Then, adjust the size and position of the crop box on the image to cut it.',
            },
            {
              heading: '3. You can also adjust the crop box by entering the values of height, width, horizontal position, and vertical position manually.',
            },
            {
              heading: '4. You can also use some premade templates:',
              bullets: [
                'Landscape(16:9)',
                'Portrait(9:16)',
                'Square(1:1)',
              ],
            },
            {
              heading: '5. Then you have to select one of the download options.',
              bullets: [
                'ZIP: Select this option if you are cropping multiple images and want to download all the cropped images as a single file.',
                'Separate: If you want to download all the cropped images separately, then select this option from toggle button.',
              ],
            },
            {
              heading: '6. Then click on the Crop & Download button to download all your cropped images according to your preset dimensions.',
            },
          ],
        },
        {
          id: 'imgcrop-card-4',
          title: '4. Benefits of Image Cropper: Image cropper tool offers several benefits in the digital world. Some are described below:',
          tocLabel: '4. Benefits of Image Cropper',
          bullets: [
            'Fitting of images in frames: Image cropper helps you fit images properly in frames. These frames could be either in a webpage or on any social media platform.',
            'Boosting UX: As proper placing of images boosts user experience, image cropper helps boost the UX to a great extent.',
            'Removing unwanted border: You can remove any unwanted borders from an image to refine it.',
            'Separating desired object: You can also separate your image from a group photo.',
            'Saving Storage: Cropping the image definitely saves storage as the raw area of the image is cut and just the desired part is saved.',
            'Optimizing Presentation: Since a normal presentation with 10 uncropped images takes almost 30MB of the storage, the same presentation with images cropped can take almost 10MB of the storage.',
          ],
        },
        {
          id: 'imgcrop-card-5',
          title: '5. Supported formats:',
          formats: ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.avif', '.heic', '.heif', '.jxl', '.bmp', '.ico', '.tiff', '.tif', '.jfif', '.jpe', '.pjpeg', '.pjp', '.apng', '.svgz', '.xbm'],
        },
      ],
    },
  },

  /* ————— Photo Editor (placeholder) ————— */
  photoEditor: {
    seo: {
      title: 'Photo Editor Online — Edit Images Free with Text, Effects & Frames | Photremium',
      desc: 'Edit photos online for free — add text, effects, frames, stickers and more. Simple yet powerful image editing tools right in your browser.',
      keywords: 'photo editor, online photo editor, edit photo online free, add text to image, image effects, photo frames',
    },
    title: 'Photo Editor',
    desc: 'Spice up your pictures with text, effects, frames and stickers. Simple editing tools for all your image needs.',
    toEdit: 'to edit',
    textTypography: 'Text & Typography',
    textTypographyDesc: 'Add custom text with various fonts, colors, shadows and positioning options.',
    filtersEffects: 'Filters & Effects',
    filtersEffectsDesc: 'Apply professional filters, adjust brightness, contrast, saturation and more.',
    framesStickers: 'Frames & Stickers',
    framesStickersDesc: 'Choose from beautiful frames and fun stickers to enhance your images.',
  },

  /* ————— Remove Background ————— */
  removeBg: {
    seo: {
      uploadTitle: 'Remove Background from Image Online Free | Photremium',
      uploadDesc: 'Quickly remove image backgrounds with high accuracy. Free online background remover — runs entirely in your browser.',
      uploadKeywords: 'remove background, background remover, remove image background, transparent background, cut out background free',
      workspaceTitle: 'Removing Backgrounds — Photremium Background Remover',
      workspaceDesc: 'Remove image backgrounds online for free. No signup required.',
      workspaceKeywords: 'remove background, background remover, transparent background',
    },
    title: 'Remove Background',
    desc: 'Instantly remove backgrounds from your images with advanced edge detection. 100% free, runs entirely in your browser — no uploads, no servers.',
    toRemoveBg: 'to remove background',
    backgroundRemover: 'Background Remover',
    processingProgress: 'Processing Progress',
    processingNImages: 'Processing {n} image(s)…',
    allNProcessed: 'All {n} image(s) processed!',
    nOfTotalDone: '{n} / {total} done',
    fileName: 'File Name',
    dimensions: 'Dimensions',
    status: 'Status',
    statusDone: '✓ Done',
    statusProcessing: '⏳ Processing',
    statusError: '✗ Error',
    statusPending: '⏸ Pending',
    badgeDone: 'Done',
    badgeProcessing: 'Processing…',
    badgeError: 'Error',
    removingBg: 'Removing background…',
    retry: 'Retry',
    downloading: 'Downloading…',
    nextImage: 'Next Image',
  },

  /* ————— Remove Background (AI) ————— */
  removeBgAi: {
    seo: {
      uploadTitle: 'AI Background Remover — Remove Background Online | Photremium',
      uploadDesc: 'Remove image backgrounds using cloud AI for higher accuracy. Upload up to 3 images at a time.',
      uploadKeywords: 'ai background remover, remove background online, iloveimg background remover',
      workspaceTitle: 'AI Background Removal — Photremium',
      workspaceDesc: 'Remove image backgrounds online using cloud AI. Upload up to 3 images at a time.',
      workspaceKeywords: 'ai background remover, remove background, cloud background removal',
    },
    title: 'AI Background Remover',
    desc: 'High-accuracy background removal powered by cloud AI. Upload up to 3 images and get clean transparent PNGs.',
    toRemoveBg: 'to remove background',
    backgroundRemover: 'AI Background Remover',
    processingProgress: 'Processing Progress',
    processingNImages: 'Processing {n} image(s)…',
    allNProcessed: 'All {n} image(s) processed!',
    nOfTotalDone: '{n} / {total} done',
    fileName: 'File Name',
    dimensions: 'Dimensions',
    status: 'Status',
    statusDone: '✓ Done',
    statusProcessing: '⏳ Processing',
    statusError: '✗ Error',
    statusPending: '⏸ Pending',
    badgeDone: 'Done',
    badgeError: 'Error',
    stageUploading: 'Uploading image…',
    stageDetecting: 'Detecting edges…',
    stageFinalizing: 'Finalizing result…',
    stageProcessing: 'Processing…',
    stageDone: 'Completed',
    retry: 'Retry',
    downloading: 'Downloading…',
    nextImage: 'Next Image',
    limitError: 'You can upload up to {n} images for AI background removal.',
  },

  /* ————— Watermark Image ————— */
  watermark: {
    seo: {
      uploadTitle: 'Watermark Image Online — Add Text & Logo Watermarks Free | Photremium',
      uploadDesc: 'Add text or image watermarks to your photos in seconds. Free, fast and private.',
      uploadKeywords: 'watermark image, add watermark, text watermark, logo watermark, image watermark tool free',
      workspaceTitle: 'Watermarking Images — Photremium Watermark Tool',
      workspaceDesc: 'Add watermarks to images online for free. No signup required.',
      workspaceKeywords: 'watermark image, add watermark, text watermark, logo watermark',
    },
    title: 'Watermark Image',
    desc: 'Add text or logo watermarks to your images. Customize font, opacity, size, position and more. Runs entirely in your browser.',
    toWatermark: 'to watermark',
    watermarkTools: 'Watermark Tools',
    addText: 'Add Text',
    addImage: 'Add Image',
    layersN: 'Layers ({n})',
    noLayers: 'No layers added yet',
    textLayer: 'Text Layer',
    imageLayer: 'Image Layer',
    watermarkDownloadAll: 'Watermark & Download All',
    watermarkDownload: 'Watermark & Download',
    nextImage: 'Next Image',
    privateMode: 'Private Mode',
    privateModeOn: 'Private Mode ON: Only move and resize watermarks on this image…',
    privateModeHint: 'Enable Private Mode to adjust only position and size…',
    privateModeActive: 'Private Mode — only move, resize, and change font size are allowed',
    selectToEdit: 'Select any image or text to start editing',
    bold: 'Bold',
    italic: 'Italic',
    underline: 'Underline',
    bgColor: 'Background Color',
    textColor: 'Text Color',
    alignLeft: 'Align Left',
    alignCenter: 'Align Center',
    alignRight: 'Align Right',
    opacity: 'Opacity',
    stroke: 'Stroke',
    strokeWidth: 'Stroke Width',
    strokeColor: 'Stroke Color',
    tileRepeat: 'Tile / Repeat',
    tilePattern: 'Tile Pattern',
    spacing: 'Spacing',
    snapToCenter: 'Snap to Center',
    duplicate: 'Duplicate',
    delete: 'Delete',
    flipHorizontal: 'Flip Horizontal',
    flipVertical: 'Flip Vertical',
    rotateLeft: 'Rotate Left',
    rotateRight: 'Rotate Right',
    cornerRadius: 'Corner Radius',
    borderStroke: 'Border / Stroke',
    borderWidth: 'Border Width',
    color: 'Color',
    style: 'Style',
    hide: 'Hide',
    show: 'Show',
    lock: 'Lock',
    unlock: 'Unlock',
    solid: 'Solid',
    dashed: 'Dashed',
    dotted: 'Dotted',
    double: 'Double',
    tileSingle: 'Single',
    tile2x2: '2×2 Grid',
    tile3x3: '3×3 Grid',
    removeAllConfirm: 'Remove all images and watermarks?',
    dimMismatch: 'The images you have uploaded have different dimensions. Try uploading images of the same resolution for best results, or apply watermark one by one.',
    clearBackground: 'Clear Background',
    typeHere: 'Type here...',
    linkCorners: 'Link corners',
    separateCorners: 'Separate corners',
    blog: {
      tocTitle: 'Contents',
      sections: [
        {
          id: 'wm-blog-intro',
          tocLabel: 'Watermark Tool Overview',
          title: 'How to Add a Watermark to Your Images Online — The Complete Guide to Photremium\'s Watermark Tool',
          paragraphs: [
            'You\'ve just finished editing a beautiful photograph. Maybe it\'s a product shot for your store, a portrait from a client session, or a piece of digital artwork you\'ve spent hours perfecting. And now comes the question every creator eventually faces: How do you protect it before sharing it?',
            'The answer most professionals land on is a watermark — and for good reason. A properly placed watermark acts as a permanent signature on your work, making it traceable, branded, and significantly harder to steal.',
            'Here\'s the problem most people run into: the tools available online are either too clunky, locked behind subscriptions, or produce such a heavy watermark that it ruins the image entirely.',
            'Photremium\'s Watermark Image tool was built to fix all of that. It\'s a fully browser-based, free, no-account-required watermarking workspace that runs directly in your browser without ever uploading your files to a server. Everything stays local, everything stays fast, and the level of creative control it offers rivals what you\'d expect from a desktop software application. In this guide, we\'re going to walk you through exactly how it works, who it\'s built for, and how to get the best possible results — every time.',
          ],
        },
        {
          id: 'wm-blog-what-is',
          tocLabel: 'What Is a Watermark',
          title: 'What Is a Watermark and Why Does It Matter?',
          paragraphs: [
            'Before diving into the tool itself, it\'s worth understanding what a watermark does and why so many photographers, designers, and content creators consider it non-negotiable.',
            'A watermark is a visible overlay added to an image — typically text, a logo, or a copyright symbol — that identifies the creator or owner of the content. Unlike metadata (which is hidden inside the file and can be stripped in seconds), a watermark is embedded visually into the image itself. You can\'t just right-click and remove it without noticeably degrading the image quality.',
          ],
          subSections: [
            {
              title: 'Why Creators Use Watermarks',
              paragraphs: [
                'The practical reasons for watermarking your images come down to three core concerns.',
              ],
              bullets: [
                'Ownership identification. When your work gets shared across social media platforms, Pinterest boards, or embedded in blog posts, the original attribution is often stripped away. A watermark ensures your name or brand travels with the image wherever it ends up.',
                'Theft deterrence. Most casual image thieves are looking for the path of least resistance. A professional-looking watermark, especially one that\'s well-positioned and slightly transparent, sends a clear signal: this image belongs to someone who cares about their work.',
                'Client portfolio protection. Photographers who share previews with clients before final payment frequently use watermarks to prevent clients from using the preview without settling the invoice. A subtle watermark achieves this without making the preview too distracting to evaluate.',
              ],
            },
            {
              title: 'Who Should Be Watermarking Their Images',
              paragraphs: [
                'If you fall into any of the following categories, watermarking your images before sharing them online isn\'t optional — it\'s essential:',
              ],
              bullets: [
                'Freelance photographers sharing galleries with clients',
                'Graphic designers distributing portfolio samples',
                'E-commerce sellers showing product photos before licensing deals',
                'Digital artists sharing work on social platforms',
                'Bloggers and content creators whose images are regularly repurposed',
                'Stock photo contributors building a recognizable brand',
              ],
            },
          ],
        },
        {
          id: 'wm-blog-workspace',
          tocLabel: 'Workspace Panels',
          title: 'Inside Photremium\'s Watermark Tool: A Three-Panel Workspace Built for Real Workflows',
          paragraphs: [
            'What makes the Photremium watermark tool stand out from basic online watermarkers is its workspace design. Rather than offering a simple "type text, pick position, done" approach, the tool provides a three-panel layout that mirrors what you\'d find in professional design software — except it runs entirely in your browser, with zero installation required.',
          ],
          subSections: [
            {
              title: 'The Left Canvas Panel',
              paragraphs: [
                'The canvas panel takes up the largest portion of the screen and shows you exactly what your image looks like with the watermark applied in real time. Every change you make — adjusting opacity, repositioning a text layer, resizing a logo — reflects instantly on the canvas. There\'s no "preview" button to click. What you see is what you get.',
                'The canvas also includes a built-in zoom toolbar, letting you zoom in for precise alignment and zoom out for the big picture. The zoom-to-fit feature automatically calculates the best initial scale based on your screen size, so even high-resolution images load at a comfortable working size. Alignment guides appear dynamically when you drag a layer near the horizontal or vertical center of the image, snapping the watermark into a perfectly centered position with a satisfying precision that you\'d normally only get from Photoshop.',
              ],
            },
            {
              title: 'The Right Settings Panel',
              paragraphs: [
                'The right panel is the control center. This is where you manage your watermark layers, customize their appearance, and trigger the final export. On desktop, it sits fixed to the right side of the screen. On mobile devices, it slides up as a bottom sheet — triggered by a floating gear button — so it never covers your canvas while you\'re working.',
                'Within the right panel you\'ll find the layer manager, which lists every watermark element you\'ve added. Each layer entry shows the layer type (text or image), its current name or content, and three quick-action buttons: a visibility toggle (the eye icon), a lock toggle to freeze a layer\'s position while you work on others, and a delete button.',
              ],
            },
            {
              title: 'The Left Preview Strip (Multi-Image Mode)',
              paragraphs: [
                'When you\'re working with multiple images at once — a batch of product photos, a full portrait session — a vertical thumbnail strip appears on the far left of the screen. This is the image queue, showing miniaturized previews of every file you\'ve loaded. Clicking any thumbnail switches your canvas to that image instantly, letting you verify how the watermark looks across different compositions without losing your layer settings.',
                'The strip also shows a size readout in KB or MB for each file, along with the file format tag (JPG, PNG, WEBP), so you always know what you\'re working with at a glance.',
              ],
            },
          ],
        },
        {
          id: 'wm-blog-steps',
          tocLabel: 'Step-by-Step',
          title: 'Step-by-Step: How to Add a Watermark to Your Image on Photremium',
          paragraphs: [
            'This walkthrough is based directly on how the tool works under the hood — from the drag-and-drop upload system to the canvas rendering engine that writes your watermark permanently onto the image before download.',
          ],
          steps: [
            {
              heading: 'Step 1 — Upload Your Image/s',
              paragraphsBefore: [
                'Head to photremium.com/watermark-image. You\'ll land on the upload screen, which presents a clean drag-and-drop dropzone in the center of the page.',
                'You have three ways to get your image into the tool:',
              ],
              bullets: [
                'Drag and drop. Drag one or more image files from your desktop directly onto the dropzone. The tool accepts any standard image format — JPEG, PNG, WEBP, and more. When you drag files over the workspace, a purple border activates around the entire area with a visual "Drop images to add for watermarking" prompt, so you know the drop is being registered.',
                'Click to browse. Click the "Choose Files" button inside the dropzone to open your system\'s file picker. You can select multiple files at once using Ctrl+Click (Windows) or Cmd+Click (Mac).',
                'Paste from clipboard. If you\'ve copied an image to your clipboard — from a screenshot, another app, or a web browser — simply press Ctrl+V (or Cmd+V on Mac) while on the watermark page and the image will load directly into the workspace. This works even when multiple images are already loaded.',
              ],
              paragraphsAfter: [
                'Once the first image loads, the workspace transitions from the upload screen to the full three-panel editor, and the canvas displays your image at an automatically calculated fit-to-screen zoom level.',
              ],
            },
            {
              heading: 'Step 2 — Add a Text Watermark Layer',
              paragraphsBefore: [
                'Click the purple "Add Text" button in the right panel. A new text layer appears on your canvas with the default placeholder text "Type here..." pre-selected.',
                'You can begin typing immediately. The text renders live on the canvas in white Arial at a default size tuned to your image dimensions. From the top toolbar that appears when the text layer is selected, you can customize every aspect of the text:',
              ],
              bullets: [
                'Font family — choose from 12 built-in fonts including Arial, Georgia, Impact, Brush Script MT, Comic Sans MS, and more',
                'Font size — type a custom pixel value directly or use the increment controls',
                'Bold, italic, underline — toggle any combination of these styles',
                'Text color — click the color swatch to open the color picker and choose any hex color',
                'Background color — add a colored background behind your text for a label or badge look',
                'Stroke (outline) — set a stroke width and color for maximum legibility on both light and dark images',
                'Opacity — slide opacity from 0% (invisible) to 100% (fully opaque); most watermarks sit between 30% and 60%',
                'Alignment — left, center, or right align the text within its bounding box',
                'Rotation — rotate the text layer to any angle, with magnetic snap points at 0°, 90°, 180°, and 270° for quick straight-angle placement',
              ],
              paragraphsAfter: [
                'To move the text, click and drag it anywhere on the canvas. For fine pixel-perfect positioning, use the arrow keys on your keyboard (hold Shift for 10-pixel steps). To resize the text box, drag any of the eight resize handles (corners and edges) that appear around the selected layer.',
              ],
            },
            {
              heading: 'Step 3 — Add an Image (Logo) Watermark Layer',
              paragraphsBefore: [
                'If you want to use a logo, signature, or any image as a watermark instead of — or in addition to — text, click the blue "Add Image" button in the right panel. Your file picker opens so you can choose the watermark image file from your device.',
                'The tool automatically sizes the watermark image to roughly 30% of your canvas width while maintaining its original aspect ratio, and centers it on the canvas. From there, you can drag it to any position, resize it using the corner handles (which constrain the aspect ratio), or rotate it using the dedicated rotate handle that appears above the selected layer.',
                'For image-type watermark layers, the right panel exposes additional controls:',
              ],
              bullets: [
                'Opacity — same as text layers, ideal for transparent PNG logos',
                'Flip horizontal / Flip vertical — mirror the watermark if needed',
                'Border width, border color, and border style — add a visible border around the logo watermark',
                'Border radius — round the corners of the logo watermark, either uniformly or with separate control over each corner',
                'Rotation — same smooth rotation behavior as text layers',
              ],
            },
            {
              heading: 'Step 4 — Configure Tiling (Optional)',
              paragraphsBefore: [
                'One of the more powerful features in the tool is tiling — the ability to repeat your watermark across the entire image in a grid pattern. This is particularly useful when you want to make an image unusable for republication rather than simply branded.',
                'With a layer selected, look for the tile option in the toolbar. You can set it to:',
              ],
              bullets: [
                'None — single placement (default)',
                'Grid 2×2 — repeats the watermark in a 2-column, 2-row pattern',
                'Grid 3×3 — repeats in a 3×3 grid for denser coverage',
              ],
              paragraphsAfter: [
                'You can also adjust the tile spacing, which controls the gap between each repeated instance. The canvas shows ghost previews of all the tiled copies so you can see the full coverage pattern before downloading.',
              ],
            },
            {
              heading: 'Step 5 — Use the Layer Manager for Multiple Watermarks',
              paragraphsBefore: [
                'You can add as many layers as you want — mixing text and image watermarks freely. The layer manager in the right panel lists them all. You can:',
              ],
              bullets: [
                'Show/hide individual layers using the eye icon — useful for toggling between different watermark versions without deleting anything',
                'Lock a layer to prevent accidental repositioning while working on other layers',
                'Delete a layer permanently',
              ],
              paragraphsAfter: [
                'Clicking any layer entry in the list selects it on the canvas simultaneously, making it easy to switch between layers without hunting around the image.',
              ],
            },
            {
              heading: 'Step 6 — Switch Between Multiple Images',
              paragraphsBefore: [
                'If you uploaded more than one image, use the thumbnail strip on the left to switch your active canvas image. The watermark layers you\'ve configured apply universally — they\'ll be rendered on every image in your batch when you export.',
                'The tool also performs a resolution consistency check across your batch. If your images have significantly different dimensions (more than 5% difference in width or height), a warning appears letting you know the watermark positioning may look different across images due to the size variation. The engine handles this gracefully through a proportional repositioning algorithm: it calculates the watermark\'s position as a percentage of the reference image\'s dimensions and applies the same proportional placement to every other image in the batch, preserving the visual intent even across different resolutions.',
              ],
            },
            {
              heading: 'Step 7 — Set Your Download Preferences',
              paragraphsBefore: [
                'For single-image jobs, click the "Download" button below the canvas to save the watermarked image directly. The filename is automatically set to your original filename with "-watermarked" appended before the extension. JPEG images are exported at 0.95 quality — high enough to be indistinguishable from the original at normal viewing sizes. PNG images are exported as lossless PNG.',
                'For batch jobs (multiple images), you have two download modes selectable in the right panel:',
              ],
              bullets: [
                'ZIP file — all watermarked images are packaged into a single ZIP archive that downloads in one click. The tool loads JSZip dynamically to handle the compression.',
                'Separate files — each image downloads as an individual file with a short delay between each download to prevent browser throttling.',
              ],
            },
            {
              heading: 'Step 8 — Download and Verify',
              paragraphsBefore: [
                'After clicking the download button (or the "Watermark & Download All" button for batches), the tool renders each image on a hidden HTML5 canvas element at full native resolution — not at the scaled preview size you see on screen. This means the exported image is at 100% of its original pixel dimensions with the watermark drawn at the proportionally correct size. Open the downloaded file and zoom in to verify the watermark placement and quality match your expectations.',
                'If anything looks off, simply return to the workspace — your layers are still there — adjust, and download again.',
              ],
            },
          ],
        },
        {
          id: 'wm-blog-tips',
          tocLabel: 'Pro Tips',
          title: 'Pro Tips for Getting the Best Results',
          bullets: [
            'Use a semi-transparent PNG logo for maximum professionalism. A logo watermark with a transparent background at 40–50% opacity looks far more polished than solid text. Export your logo from your design tool as a PNG with alpha transparency before using it as an image layer.',
            'Place your watermark where cropping it removes value. The bottom-right corner is the most common watermark placement — and the easiest to crop out. For stronger protection, consider placing your watermark across a visually important area of the image, such as over a face, a product detail, or the focal point. The diagonal tile option is even harder to remove without significantly degrading the image.',
            'Use the lock feature when working with multiple layers. Once you\'re happy with your primary watermark position, lock that layer before adding secondary layers. This prevents accidentally nudging it while you work on other elements.',
            'Test across light and dark areas of your image. A white watermark is invisible on a bright sky but perfectly readable on a dark shadow. Add a subtle stroke or background color to ensure legibility regardless of what the image contains beneath the watermark.',
            'Keyboard shortcuts always save time. Arrow keys move selected layers one pixel at a time. Shift+Arrow moves ten pixels at a time. The Delete key removes the selected layer instantly. These shortcuts make fine-tuning placement much faster than dragging.',
          ],
        },
        {
          id: 'wm-blog-privacy',
          tocLabel: 'Privacy and Security',
          title: 'Privacy and Security: Why Browser-Based Processing Matters',
          paragraphs: [
            'One concern many creators have when using online tools is where their images actually go. With server-based watermarking tools, your image is uploaded to the provider\'s server, processed there, and then sent back to you. That means your unpublished work, client photos, or sensitive product images pass through someone else\'s infrastructure.',
            'Photremium\'s watermark tool processes everything locally in your browser. Your images are loaded into your device\'s memory using the browser\'s File API and are never transmitted over the internet. The watermark rendering happens on an HTML5 canvas element running in JavaScript, entirely within your browser tab. When you close the page or click "Start Over," all image data is released from memory and discarded. Nothing is stored anywhere outside your own device.',
          ],
        },
        {
          id: 'wm-blog-summary',
          tocLabel: 'Final Summary',
          title: 'Why Photremium\'s Watermark Tool Earns a Place in Your Regular Workflow',
          paragraphs: [
            'There are dozens of watermarking tools available online. What separates a tool worth bookmarking from one worth forgetting is usually a combination of speed, control, and trustworthiness.',
            'Speed matters because watermarking shouldn\'t add significant time to your post-processing workflow. The Photremium tool loads instantly, processes locally, and exports at full resolution in seconds — even for large batches. There\'s no waiting for server-side processing, no queue, no rendering time that scales with the number of users online at the same time.',
            'Control matters because a watermark that looks wrong is worse than no watermark at all — it makes your brand look unprofessional. The layer system, the per-property controls in the toolbar, the real-time canvas preview, and the keyboard shortcut support all add up to a level of control that\'s genuinely comparable to dedicated desktop software.',
            'Trustworthiness matters because your unpublished work is valuable. Knowing that your images never travel over the internet to a third-party server means you can watermark client photos, unreleased product images, and sensitive creative work without any concern about data handling, server-side breaches, or policy changes by a cloud provider.',
            'Whether you\'re adding a quick copyright notice to a single photo or processing an entire shoot of 200 images before sharing a client gallery, Photremium\'s Watermark Image tool gives you the tools to do it right — completely free, completely private, and with enough creative flexibility to make the watermark look like it belongs on the image rather than stuck on top of it.',
            'Head over to photremium.com/watermark-image and give it a try. Your first watermarked image is literally thirty seconds away.',
            'Photremium offers a suite of browser-based image editing and processing tools. All tools run locally in your browser — your images stay on your device.',
          ],
        },
      ],
    },
  },

  /* ————— QR Code Generator ————— */
  qrGenerator: {
    seo: {
      inputTitle: 'QR Code Generator — Create & Customize Free | Photremium',
      inputDesc: 'Generate custom QR codes for URLs, text, WiFi, contacts, email and Google Maps locations. Customize colors, shapes, frames, logos and download in multiple formats.',
      inputKeywords: 'qr code generator, create qr code, custom qr code, qr code maker, qr code free',
      customizeTitle: 'Customize QR Code — Photremium QR Code Generator',
      customizeDesc: "Customize your QR code's style, color, frame and logo. Download in PNG, JPG, SVG, WEBP or PDF.",
      customizeKeywords: 'customize qr code, qr code style, qr code colors, qr code logo',
    },
    title: 'QR Code Generator',
    desc: 'Generate, customize and download QR codes for URLs, text, WiFi, contacts, email & maps. Fully free, runs in your browser.',
    generate: 'Generate',
    scanQrCode: 'Scan QR Code',
    tabUrl: 'Link / URL',
    tabText: 'Text',
    tabWifi: 'WiFi',
    tabContact: 'Contact',
    tabEmail: 'Gmail',
    tabMaps: 'Google Maps',
    enterUrl: 'Enter URL',
    enterText: 'Enter Text',
    networkName: 'Network Name (SSID)',
    password: 'Password',
    encryption: 'Encryption',
    hiddenNetwork: 'Hidden Network',
    contactName: 'Contact Name (optional)',
    phoneNumber: 'Phone Number',
    emailAddress: 'Email Address',
    subject: 'Subject (optional)',
    body: 'Body (optional)',
    searchLocation: 'Search Location',
    urlPlaceholder: 'https://example.com',
    textPlaceholder: 'Type your text here…',
    wifiNamePlaceholder: 'My WiFi',
    passwordPlaceholder: 'Password',
    contactPlaceholder: 'John Doe',
    phonePlaceholder: '+1 234 567 8900',
    emailPlaceholder: 'example@gmail.com',
    subjectPlaceholder: 'Subject',
    bodyPlaceholder: 'Email body…',
    locationPlaceholder: 'Search city, address, place…',
    wpaWpa2: 'WPA / WPA2',
    wep: 'WEP',
    none: 'None',
    characters: 'characters',
    qrGettingComplex: 'QR code is getting complex — may be hard to scan.',
    qrTooComplex: 'QR code is too complex! Most scanners will fail to read it.',
    generateCustomize: 'Generate & Customize',
    editQrData: 'Edit QR Data',
    styleShape: 'Style & Shape',
    colorTab: 'Color',
    framesTab: 'Frames',
    logoTab: 'Logo',
    templatesTab: 'Templates',
    fileFormatTab: 'File Format',
    dotShape: 'Dot Shape',
    cornerEyeOuter: 'Corner Eye — Outer',
    cornerEyeInner: 'Corner Eye — Inner',
    fgColor: 'Foreground Color',
    bgColor: 'Background Color',
    enableGradient: 'Enable Gradient',
    gradientType: 'Gradient Type',
    linear: 'Linear',
    radial: 'Radial',
    gradientColor1: 'Gradient Color 1',
    gradientColor2: 'Gradient Color 2',
    suggestedGradients: 'Suggested Gradients',
    selectFrame: 'Select Frame',
    selectLogo: 'Select Logo',
    uploadCustomLogo: 'Or Upload Custom Logo',
    uploadImage: 'Upload Image',
    customLogoApplied: 'Custom logo applied',
    premadeTemplates: 'Premade Templates',
    templateHint: 'Click a template to instantly apply its style combination.',
    downloadFormat: 'Download Format',
    fgBgIdentical: 'Foreground and background colors are identical — QR code will be invisible!',
    fgBgSimilar: 'Foreground and background colors are very similar — QR code may be hard to scan.',
    preview: 'Preview',
    angle: 'Angle',
    downloadFormatBtn: 'Download {format}',
    pngDesc: 'Lossless, transparent support',
    jpgDesc: 'Popular/universal file format',
    webpDesc: 'Modern format, best quality/size',
    svgDesc: 'Scalable vector graphic',
    pdfDesc: 'Print-ready document',
    lat: 'Lat:',
    lng: 'Lng:',
    qrCode: 'QR Code',
    scanMe: 'SCAN ME',
    scanHere: 'SCAN HERE',
    blog: {
      card1: {
        title: '1. What is a QR code?',
        intro1: 'The full form of QR code is “Quick Response code.” Since the information stored in it is decoded lightning fast, it is called Quick Response (QR) code.',
        intro2: 'QR code is a two-dimensional digital code readable by machines only. The data in it cannot be red by a human. It contains blocks to store information like text, contact numbers, links, and google map locations, etc.',
        inventionTitle: 'How the QR code was invented:',
        inventionText: 'QR code was invented in 1994. Masahiro Hara and his team were working in a Japanese company named Denso Wave, that made car parts for Toyota. They wanted a code that stores much more information than a typical barcode, then they invented QR code.',
        keyFeaturesTitle: 'Key features of a QR code',
        keyFeatures: [
          {
            title: 'How QR works',
            text: 'You enter the text or link to generate a QR code of it. The QR generator makes square boxes according to the information. Each box represents a bit, and each bit stores a character.',
          },
          {
            title: 'Structure of QR code',
            text: 'A QR code contains three big square boxes to identify the orientation of the QR code, and a small square box to store the bits (information).',
          },
          {
            title: 'Data Capacity of QR code',
            text: 'Typically, a QR code stores more information than a bar code. QR code is simply an advanced version of bar code. It is basically a 2-dimensional bar code. A QR code stores almost 200 characters. It can also store 400 characters but may be hard to scan. Storing more than 400 characters may result in a very complex QR code which cannot be scanned mostly.',
          },
          {
            title: 'Error Detection in QR codes',
            text: 'A QR code can be still scannable if some of the information on it is scratched or some bits are flipped. QR code uses Reed-Solomon error detection technique to detect the errors, and resolves them after identification.',
          },
        ],
        errorTitle: 'QR code’s error correction level:',
        errorIntro: 'Different error detection and correction techniques are used in different cases as described below:',
        errorHeaders: {
          level: 'Level',
          recovery: 'Recovery capacity',
          bestUse: 'Best use case',
        },
        errorRows: [
          {
            level: 'Low(L)',
            recovery: '~7%',
            useCases: [
              'This error correction technique is used when QR code is displayed on digital screens, websites, or in a clean environment.',
              'When a QR code is generated for small information like Gmail, contact number, or small text.',
            ],
          },
          {
            level: 'Medium(M)',
            recovery: '~15%',
            useCases: [
              'For flyers and posters that are installed in clean environment, and when damage risk is negligible.',
            ],
          },
          {
            level: 'Quartile(Q)',
            recovery: '~25%',
            useCases: [
              'When QR code is to be installed in the harsh environment like in factory/outdoor.',
              'Also, when a small logo is needed in the center, it covers some bits. So, this error correction technique is needed.',
            ],
          },
          {
            level: 'High(H)',
            recovery: '~30%',
            useCases: [
              'Used when a heavy damage to the QR code is possible.',
              'When large logos cover the center of QR code.',
              'Also, when heavy styling is needed for QR code.',
            ],
          },
        ],
      },
      card2: {
        title: '2. Why choose our QR code generator?',
        intro: 'Our QR code generator makes QR code generation extremely fast, secure, and easy. You can customize your code to make it more advanced, professional, and matching with your business theme. All customizations from coloring the QR code to logo installation in the QR code are offered freely forever.',
        strengths: [
          {
            title: 'Professional Customization:',
            text: 'You can generate and customize as many QR codes as you need without any cost. All features are fully free. You can apply gradient colors, different shapes for square boxes, custom logos, and many other features.',
          },
          {
            title: 'Multiple formats support:',
            text: 'Our QR code generator offers multiple download formats for your ease. You just have to select your preferred format before downloading. Currently we offer these formats:',
          },
          {
            title: 'Pre-made templates:',
            text: 'You can use pre-made gradient colors and frames that make your QR code professional.',
          },
          {
            title: 'Custom logo support:',
            text: 'You can also upload your custom brand logo that fits automatically in the center of QR code. Also, you can select logo from in-built logo library where more than 30 logos are provided.',
          },
          {
            title: 'Live Preview:',
            text: 'While customizing, you can watch all the changes in a live preview panel. These changes in the preview panel are real-time that change dynamically.',
          },
          {
            title: 'Scan Indicator:',
            text: 'While choosing colors, proper warnings and indicators guide you if you select the colors that are hard to scan.',
          },
        ],
        formatHeaders: {
          srNo: 'Sr. No',
          formats: 'Formats',
          description: 'Description',
        },
        formatRows: [
          { no: '1', format: 'PNG', description: 'Lossless, transparent support' },
          { no: '2', format: 'JPG', description: 'Popular, Universal file format' },
          { no: '3', format: 'WEBP', description: 'Modern, Best quality/size' },
          { no: '4', format: 'SVG', description: 'Scalable vector graphic' },
          { no: '5', format: 'PDF', description: 'Print-ready document' },
        ],
      },
      card3: {
        title: '3. Step by step guide:',
        step1Intro: 'First of all, select the category of the QR code you want to generate. Either you are generating QR code for contact, Map, link or text. Currently, our QR code generator offers these categories:',
        categories: ['Link / URL', 'Text', 'WiFi', 'Contact', 'Gmail', 'Google Maps'],
        step2: 'After selecting the category, paste your information in the text box accordingly.',
        step3Intro: 'Then, click on Generate & Customize to open the customization panel. In the customization panel, you will see a sub navigation bar with the following options:',
        options: [
          {
            title: 'Style & Shape:',
            text: 'In this section, you can change the shapes of square boxes to multiple styles like rounded corners, dots, horizontal bars, vertical bars, small squares, and many others.',
          },
          {
            title: 'Color:',
            text: 'You can select your custom background and foreground colors in this section. To make your QR code in gradient colors, you have the option to enable gradient which lets you apply any gradient color from in-built library or make your own gradient color easily. You can also change the angle for linear gradient color. Radial gradient is also supported. You can add multiple colors to make a professional gradient effect.',
          },
          {
            title: 'Frames:',
            text: 'Here, you can select a perfect frame for your QR code. All frames automatically fit into your code automatically. You just have to select a frame from the inbuilt library.',
          },
          {
            title: 'Logo:',
            text: 'After frame selection, you can select a logo that is automatically adjusted in the center of QR code. Uploading of your custom brand logo is also supported.',
          },
          {
            title: 'Templates:',
            text: 'If you want not to select style, shape, color and frame separately, you can apply the premade templates on your QR code. Multiple premium templates are provided for your ease that are free forever.',
          },
          {
            title: 'File Format:',
            text: 'Finally, you have to select the file type in which you want to download the QR code. Several common formats are supported including PNG, WEBP, SVG, and PDF, etc.',
          },
          {
            title: 'Edit QR Data:',
            text: 'You can also edit your QR data without any loss in your selected customizations.',
          },
        ],
        finalStep: 'Then, just click on the download button, and the QR code exactly according to your customizations and in the selected format will be downloaded instantly without any cost.',
      },
      card4: {
        title: '4. Benefits:',
        intro: 'QR code connects the physical world with the digital experience. It is the fastest way to share links, contact, or your location. You just have to scan that QR code to access the information instantly. So, it is very popular in businesses and digital world for being time-efficient and highly purposeful.',
        benefits: [
          {
            title: 'Lifetime Support:',
            text: 'QR codes work forever without any expiry. They give you the information stored instantly when you scan it. Since it stores hardcoded digital instructions, it never loses its information.',
          },
          {
            title: 'Offline Support:',
            text: 'You can scan the QR code even if you are offline. Your mobile camera decodes the patterns of the QR code and gives you the information stored in it.',
          },
          {
            title: 'Zero cost:',
            text: 'Since these codes do not require any server redirection or resources, it is free of cost forever.',
          },
        ],
      },
    },
  },

  /* ————— QR Code Scanner ————— */
  qrScanner: {
    seo: {
      title: 'QR Code Scanner — Scan QR Codes from Camera or Image | Photremium',
      desc: 'Scan QR codes instantly using your camera or by uploading an image. Detect URLs, text, WiFi, contacts and more. Free, fast, private.',
      keywords: 'qr code scanner, scan qr code, qr reader, qr code reader online, scan qr from image, camera qr scanner',
    },
    title: 'QR Code Scanner',
    desc: 'Scan QR codes instantly using your camera or upload an image. Fast, private & secure — everything runs in your browser.',
    generateQrCode: 'Generate QR Code',
    scan: 'Scan',
    cameraScanner: 'Camera Scanner',
    uploadImage: 'Upload Image',
    pointCamera: 'Point your camera at a QR code',
    startCamera: 'Start Camera',
    loading: 'Loading…',
    dropQrImage: 'Drop QR code image here',
    toScan: 'to scan',
    supportedFormats: 'Supports JPG, PNG, GIF, WEBP',
    ctrlVPaste: 'Ctrl+V to paste',
    scanFromGallery: 'Scan from Gallery',
    instantScanning: 'Instant Scanning',
    instantScanningDesc: 'Real-time QR code detection from camera or uploaded images',
    private100: '100% Private',
    private100Desc: 'All processing happens in your browser. Nothing is uploaded',
    multiFormat: 'Multi-Format',
    multiFormatDesc: 'Detect URLs, text, WiFi, contacts, email, phone & more',
    accessingCamera: 'Accessing camera…',
    cameraDenied: 'Camera access denied. Please allow camera permission and try again.',
    noCamera: 'No camera found on this device.',
    cameraError: 'Could not access camera. Please try again.',
    videoError: 'Could not start video playback.',
    switchError: 'Could not switch camera.',
    tryAgain: 'Try Again',
    switchCamera: 'Switch Camera',
    stop: 'Stop',
    noQrFound: 'No QR Code Found',
    couldNotRead: 'Could not read image',
    scannerFailed: 'Scanner failed to load',
    noQrFoundDesc: 'No QR code was detected in the image. Please try another image or use the camera scanner.',
    qrScanned: 'QR Code Scanned!',
    openLink: 'Open Link',
    copied: 'Copied!',
    copy: 'Copy',
    share: 'Share',
    scanAgain: 'Scan Again',
    network: 'Network',
    password: 'Password',
    security: 'Security',
    unknown: 'Unknown',
    typeText: 'Text',
    typeUrl: 'URL',
    typeWifi: 'WiFi Network',
    typeContact: 'Contact Card',
    typeEmail: 'Email',
    typePhone: 'Phone Number',
    typeSms: 'SMS',
    typeLocation: 'Location',
    blog: {
      tocTitle: 'Contents',
      sections: [
        {
          id: 'why-use-qr-code-scanner',
          title: '1. Why use QR code scanner?',
          paragraphs: [
            'QR code scanner is basically a mobile camera that decodes the information stored in the QR code. You just have to point the camera towards the QR code, regardless of the orientation of QR code, it will read the specific patterns, detect errors, correct the errors if required, and display the information in it.',
          ],
          listTitle: 'Types of QR code scanners:',
          bullets: [
            'Web-Based QR code scanner: You can scan the QR code with online scanners that your browser supports. You can access the information through camera or scanning via uploading the image. For auto-redirection and other advanced features, you can visit https://photremium.com.',
            'Mobile cameras: Some modern mobiles have built-in QR code scanner in their original camera apps. These can fetch and decode the information just by scanning the QR code through simple camera.',
            'Apps: There are some apps built specifically to scan the QR codes. However, for advanced redirection and many other features, you can install app through this link for free: https://photremium.com.',
            'Hardware devices: Some hardware devices are also made to scan QR codes. These devices are widely used in big shopping malls for billing of items.',
          ],
        },
        {
          id: 'why-choose-our-qr-code-scanner',
          title: '2. Why choose our QR code scanner?',
          paragraphs: [
            'You can explore multiple advanced features including redirection to copying the content decoded from the QR code. You can explore many other interesting features in our QR code scanner as:',
          ],
          bullets: [
            'Fast Speed: Our QR code scanner makes the decoding of QR code extremely fast. You just have to scan the QR code, and out tool gives you the information instantly with advanced option to copy the text/link, open if the link is detected, or automatically save if a contact is detected.',
            'No Installation Needed: You don’t have to install any separate app to access our QR code scanner. You can access this tool via your local browser for free.',
            'Cross-Platform Access: You can access it on any device. No worry about the OS version or any other requirement. This is supported on all versions and all devices.',
            'All Formats Supported: All data formats that could be stored in the QR code including plain text, WiFi, URLs, contacts, Gmail, and Google Maps, etc are supported by our tool.',
          ],
        },
        {
          id: 'step-by-step-guide',
          title: '3. Step by step guide:',
          steps: [
            {
              heading: '1. First of all, choose a method to scan the QR code. There are following two methods to do it:',
              subSteps: [
                {
                  heading: 'i. Camera method',
                  paragraphs: [
                    'You can scan the QR code using your device camera even when you are using a laptop, a PC, or any device that supports a simple camera. Just clicking on the “Start Camera” button will pop a permission request, allow the permission for this site to use your camera for QR scanning. If you have accidently declined or the permission request is not showing, you can grant camera access manually by following these steps:',
                  ],
                  notes: [
                    '➔ For Chrome (Android / Desktop):',
                    '1. Click on three dots',
                    '2. Go to Settings',
                    '3. (Skip this step for Android) Privacy and Security',
                    '4. Click on Site Settings',
                    '5. Then, in permissions section, click on Camera',
                    '6. Find the URL of our site as https://photremium.com/qr-code-scanner',
                    '7. Allow camera from the drop down.',
                    '➔ For Safari (iPhone / iOS):',
                    '1. Tap the AA or Website Settings icon in the address bar.',
                    '2. Select Website Settings.',
                    '3. Tap Camera and choose Allow.',
                    '4. If it’s still blocked: Go to your iPhone Settings > Safari > Camera and set it to Ask or Allow.',
                  ],
                },
                {
                  heading: 'ii. Gallery method',
                  paragraphs: [
                    'You can upload the image of a QR code to access its information. Click on the “Scan from Gallery” button, and it will open the file explorer. Then, select the QR code image. All image formats are supported such as jpg, .jpeg, .png, .gif, .webp, .svg, .avif, .heic, .heif, .jxl, .bmp, .ico, .tiff, .tif, .jfif, .jpe, .pjpeg, .pjp, .apng, .svgz, and .xbm, etc.',
                  ],
                },
              ],
            },
            {
              heading: '2. When you select the QR code, all the information is displayed; allowing you to copy, share, or open the link if detected. You can also scan again if you want to.',
            },
          ],
        },
        {
          id: 'benefits-of-qr-code-scanner',
          title: 'Benefits of QR code scanner:.',
          paragraphs: [
            'QR code scanner offers several benefits in this digital world. It makes the payments, verifications, and access to information easy and speedy even in the offline mode.',
          ],
          bullets: [
            'Instant access to the information: Scanning the QR code will provide the information in a matter of milli-seconds. It decodes the patterns, detects errors, and removes them real quick, providing the user an extremely fast experience.',
            'Seamless Communication: In this era of advancement, there is a dire need of fast and secure communication for small information like bank details and product details, etc. QR code scanner solve this problem as payments are done by scanning a QR code. Also, Google verifies the mobile phone via QR when creating a new account on laptop.',
            'E-Tickets: At bus stations, railway stations, or airports, you can easily verify your tickets without waiting for your turn in queues for a long time. Moreover, QR code scanners are installed at multiple sites at airports to avoid waiting queues. This also minimizes the need of employees, thus automatically saving the airline budget.',
            'Product Authentication: Advanced and high-end brands install a QR code on their product. Customer can scan that QR code to check the authenticity of the product whether it is original or not.',
          ],
        },
      ],
    },
  },

  /* ————— Face Blur ————— */
  faceBlur: {
    seo: {
      uploadTitle: 'Face Blur Tool — Blur Faces in Images Online | Photremium',
      uploadDesc: 'Blur faces in your images automatically with AI face detection. Choose blur intensity, shape, or emoji overlays. 100% free, fast & private.',
      uploadKeywords: 'face blur, blur faces, face detection, privacy tool, blur face online free',
      workspaceTitle: 'Face Blur Tool — Blur Faces in Images Online | Photremium',
      workspaceDesc: 'Blur faces in your images automatically. 100% free, fast & private.',
      workspaceKeywords: 'face blur, blur faces, face detection',
    },
    title: 'Face Blur',
    desc: 'Automatically detect and blur faces in your images for privacy. Choose blur intensity, shape, or cover faces with fun emojis. 100% client-side — your images never leave your device.',
    toBlurFaces: 'to blur faces',
    autoDetection: 'Auto Detection',
    autoDetectionDesc: 'AI-powered face detection finds all faces instantly',
    customBlur: 'Custom Blur',
    customBlurDesc: 'Adjust blur intensity, shape, and style',
    private100: '100% Private',
    private100Desc: 'All processing happens locally in your browser',
    blurTools: 'Blur Tools',
    faces: 'Faces',
    faceDetection: 'Face Detection',
    detecting: 'Detecting...',
    autoDetectFaces: 'Auto-Detect Faces',
    exitManualMode: 'Exit Manual Mode',
    manualSelect: 'Manual Select',
    nFaceRegions: '{n} face region(s) found',
    noFacesDetected: 'No faces detected — use manual mode',
    manualHint: 'Click and drag on the image to draw a rectangle over the area you want to blur.',
    blurType: 'Blur Type',
    blur: 'Blur',
    emoji: 'Emoji',
    blurIntensity: 'Blur Intensity',
    strength: 'Strength',
    blurShape: 'Blur Shape',
    rectangle: 'Rectangle',
    ellipse: 'Circle',
    circle: 'Circle',
    chooseEmoji: 'Choose Emoji',
    blurRegionsN: 'Blur Regions ({n})',
    faceN: 'Face {n}',
    manualRegionN: 'Manual Region {n}',
    manualN: 'Manual {n}',
    clearAllRegions: 'Clear All Regions',
    processingDots: 'Processing...',
    downloadBlurred: 'Download Blurred Image',
    downloadNImages: 'Download {n} Images',
    downloadDialogTitle: 'Not all images edited',
    downloadDialogText: 'You have edited {edited} out of {total} images.',
    downloadAll: 'Download All',
    removeRegion: 'Remove region',
    region: 'Region',
    notAllEdited: 'Not all images edited',
    editedXofY: 'You have edited {edited} out of {total} images.',
    nextImage: 'Next Image',
  },

  /* ————— FAQ items (for translation) ————— */
  faqData: {
    imageConverter: [
      { q: 'Which image formats are supported for conversion?', a: 'We support JPG, PNG, WEBP, SVG, GIF, BMP, ICO, and TIFF. You can convert between any of these formats instantly in your browser.' },
      { q: 'Will the image quality be affected during conversion?', a: 'For lossless formats like PNG, quality is fully preserved. For lossy formats like JPG and WEBP, we use optimized encoding settings to maintain the best possible visual quality while keeping file sizes efficient.' },
      { q: 'Is there a file size or quantity limit?', a: 'There is no hard limit — you can upload multiple images at once and batch-convert them. Because everything runs locally in your browser, speed depends on your device rather than a server.' },
      { q: 'Is my data safe? Are images uploaded to a server?', a: 'Absolutely. All processing happens 100% in your browser. Your images never leave your device, ensuring complete privacy and security.' },
      { q: 'Can I convert transparent PNG images to JPG?', a: 'Yes. Since JPG does not support transparency, transparent areas will be filled with a white background by default, producing a clean result.' },
    ],
    imageCompressor: [
      { q: 'Will compressing the image reduce its quality?', a: 'No, compressing the image doesn’t affect the image quality at all until and unless you compress it beyond prescribed limits. Proper indicators guide you about the quality: i. 1 to 40% compression: You will see a green indicator with “Good quality” message. ii. 41 to 60% compression: Yellow signal visible with “Normal quality” message. Compression within this range is recommended for great results. iii. 61 to 100% compression: The system flags a red warning showing a “Bad quality” message. This range may noticeably affect the quality of the image which can be clearly noticed by human eye.' },
      { q: 'What image formats are supported for compression?', a: 'Our image compressor tools support all these listed formats:\n.jpg .jpeg .png .gif .webp .svg .avif .heic .heif .jxl .bmp .ico .tiff .tif .jfif .jpe .pjpeg .pjp .apng .svgz .xbm' },
      { q: 'Can I compress multiple images at once?', a: 'Absolutely, yes. You can compress multiple images (even 100+) at once if your system supports. As compressing the images will use your system resources, you need to have a high-performance laptop, pc, or a mobile to compress multiple images at once. If you have a low-end device, you can compress images in batches as this tool is free to use even if you compress many images in multiple batches.' },
      { q: 'Do you store or upload our images to your servers?', a: 'No, we never upload or store your images to our servers. All the images are compressed locally on your browser. Your images never leave your device. So, you can compress your personal or private images stress-free.' },
      { q: 'Is this image compressing tool free to use?', a: 'Yes, our image compressor tool is totally free to use forever. We will never ask you for any payment. There are no hidden charges. All feature including multiple image compressing are totally free.' },
    ],
    cropImage: [
      { q: 'Will cropping the image reduce its quality?', a: 'No, Cropping the image will never affect the visual quality of the image at all. Cropping the image just discards the unwanted bits. It never changes the pixels of the part of image to be cropped. So, you can crop your images in their original quality without any pixel damage.' },
      { q: 'What image formats are supported for image cropper?', a: 'You can select any image of any format as our image cropper tool offers almost all the image formats. These are listed below:\n.jpg .jpeg .png .gif .webp .svg .avif .heic .heif .jxl .bmp .ico .tiff .tif .jfif .jpe .pjpeg .pjp .apng .svgz .xbm' },
      { q: 'Do my images upload to your server?', a: 'No, all the processing will be done on your local browser. Your images will never be uploaded to our server as they never leave you device. So, you can crop your personal images with our image cropper without any risk.' },
      { q: 'Can I crop multiple images at once?', a: 'Yes, you can crop multiple images (even 100+) at once if your system supports. As cropping the images uses your system resources, so, if you have a high-performance laptop, PC, or a mobile, you can crop many images at once. If you are having a low-end device, you can crop images in batches as this tool is free to use even if you crop multiple images at once.' },
      { q: 'Is this image cropper tool free to use?', a: 'Yes, this image cropping tool is totally free to use forever. You can access this tool anywhere, anytime, and using any device. You will not only experience a responsive design for all the devices but also never asked to pay for any premium feature. All features are totally free with no hidden charges.' },
    ],
    resizeImage: [
      { q: 'Can I resize multiple images at once?', a: 'Absolutely, our image resize tool allows you to resize multiple images at once. You just upload as multiple images as your device supports, our AI powered engine will resize your all images at once. But if you have a low-end device, try resizing images in multiple batches to avoid any lag issue. Resizing the image takes device resources to prevent any server uploads to protect your image privacy.' },
      { q: 'Is this image resizing tool free to use?', a: 'Yes, this tool is free to use forever. You will never be asked about any paid subscription to unlock any premium features. There are no hidden charges demanded to download your resized images.' },
      { q: 'Will resizing the image reduce its quality?', a: '✅Decrease Size (downscale): Decreasing the size of image will never reduce the visual quality of the image. Rather, the resized image looks more sharper than the original. So, resizing the image to small dimensions than original will never reduce its quality.\n❌Increase Size (upscale): Increasing the size above the original size may affect the visual quality of the image. Severity depends up on the increased size of the image. Quality drops as much as you increase its size.' },
      { q: 'Is this image resizing tool safe to use?', a: 'Yes, our image resizer tool is totally safe to use especially for your personal and private images as all of your images are processed locally. None of your data will leave your device in any way for any processing. So, you can resize your personal images without any risk.' },
      { q: 'What image formats are supported?', a: 'All image formats are supported for our image resizer tool. You can select image in these formats:\n.jpg .jpeg .png .gif .webp .svg .avif .heic .heif .jxl .bmp .ico .tiff .tif .jfif .jpe .pjpeg .pjp .apng .svgz .xbm' },
    ],
    removeBackground: [
      { q: 'How does background removal work?', a: 'We use an advanced AI model that runs directly in your browser to detect the subject and separate it from the background, producing a transparent PNG.' },
      { q: 'Does it work with complex backgrounds?', a: 'Yes. The AI model handles a wide variety of scenes including busy backgrounds, gradients, and similar-color subjects. Results are best with clear subject–background contrast.' },
      { q: 'Can I remove backgrounds from multiple images at once?', a: 'Yes. Upload several images and the tool will process them sequentially. You can download all results as a ZIP or one at a time.' },
      { q: 'What format is the output?', a: 'The output is always a PNG file with a transparent background, which you can layer on top of any design.' },
      { q: 'Are my images sent to a server?', a: 'No. The AI model runs entirely in your browser using WebAssembly. Your images remain on your device at all times.' },
    ],
    watermarkImage: [
      { q: '1. Is the Photremium Watermark tool completely free to use?', a: 'Yes, the watermark tool at photremium.com/watermark-image is free to use with no account required and no subscription needed. You can watermark single images or entire batches without hitting a paywall, without seeing a forced registration prompt, and without any download limits. The tool is funded by making the overall Photremium platform valuable enough that users choose to come back — not by gating individual features.' },
      { q: '2. How many images can I watermark at once?', a: 'The tool supports batch watermarking of multiple images simultaneously. You can drag and drop an entire folder of images onto the upload zone, or use Ctrl+Click in the file picker to select dozens of files at once. The left-side thumbnail strip holds all of them, and when you click "Watermark & Download All," the tool renders every single image with your configured watermark layers before packaging them into a single ZIP file (or downloading them as separate files — your choice). In practice, the batch size is limited by your device\'s available memory rather than any artificial cap in the tool.' },
      { q: '3. What image formats are supported for input and output?', a: 'The input side accepts any image format your browser\'s File API can handle, which in practice means JPEG, JPG, PNG, WEBP, GIF (static), BMP, and SVG. For output, the format matches the input: if you upload a PNG, you get a PNG back; if you upload a JPEG, you get a JPEG at 0.95 quality. This format-preservation approach ensures you don\'t end up converting a lossless PNG to a lossy JPEG by accident.' },
      { q: '4. Will my watermark look the same across images with different resolutions?', a: 'Yes, through the tool\'s proportional repositioning engine. When you have a batch of images with different dimensions, the watermark is authored on the reference image (whichever you had selected in the canvas). When exporting, the engine calculates the center of gravity of each watermark layer on the reference image, expresses that position as a fractional offset from the image center, and applies the same proportional offset on each target image. The watermark\'s size also scales proportionally using the smaller of the two scale factors (width ratio vs. height ratio) to ensure it never overflows the image boundaries. The result is a watermark that maintains its relative visual position and size across your entire batch, even if image dimensions vary.' },
      { q: '5. Can I use my own logo or signature as a watermark instead of text?', a: 'Absolutely. The "Add Image" button in the right panel lets you upload any image file from your device to use as a watermark layer. PNG files with transparent backgrounds work best — the transparency is preserved, so only your logo artwork appears without a white or colored box behind it. You can resize the logo by dragging its corner handles (which maintain the aspect ratio automatically), rotate it, flip it, adjust its opacity, and even add a border or rounded corners. Many users keep a prepared transparent-background version of their logo specifically for watermarking purposes.' },
      { q: '6. How do I make my watermark transparent or semi-transparent?', a: 'Select the watermark layer on the canvas by clicking it. In the top toolbar that appears, look for the opacity control — it displays a percentage value and lets you type a number directly or scrub the value. For a subtle, professional watermark that doesn\'t overwhelm the image, 30–50% opacity is the sweet spot most creators use. For a more assertive copyright notice that\'s harder to overlook, 70–80% opacity works well. You can also animate a quick comparison by toggling the layer\'s visibility in the layer manager (the eye icon) to see the image with and without the watermark.' },
      { q: '7. Can I add multiple watermarks to a single image?', a: 'Yes — the layer system supports an unlimited number of watermark layers, each independently configurable. You can have a text copyright notice in the bottom-right corner, a logo in the top-left, and a tiled "SAMPLE" overlay all simultaneously. Each layer has its own opacity, rotation, position, size, and styling settings. The layer manager in the right panel keeps them organized, and you can toggle individual layers on and off without deleting them, making it easy to experiment with different combinations before finalizing.' },
      { q: '8. What does the tiling feature do and when should I use it?', a: 'Tiling repeats your watermark in a grid pattern across the entire image. The Grid 2×2 option places four copies in a two-by-two arrangement; Grid 3×3 places nine copies in a three-by-three arrangement. The tile spacing control adjusts the gaps between copies. Tiling is most useful when you want to make an image unusable without authorization — a single-placement watermark is relatively easy to crop away if it\'s in a corner, but a full-coverage tile pattern makes the image essentially unusable without the watermark. This is the approach used for sharing contract previews, draft illustrations for client approval, or any content that shouldn\'t be freely usable before payment.' },
      { q: '9. Does the tool work on mobile phones and tablets?', a: 'Yes, the tool is fully responsive and designed for mobile use. On screens narrower than 900 pixels, the three-panel layout switches to a stacked layout: the image thumbnail strip becomes a horizontal scrollable row at the top, the canvas fills the main area, and the settings panel becomes a bottom sheet that slides up when you tap the floating gear button. All interactive elements — layer dragging, resizing handles, rotation — are touch-enabled with touchstart and touchmove events. The resize handles are slightly larger on mobile (16×16 pixels versus the desktop size) to make them easier to tap accurately with a finger.' },
      { q: '10. Is my image data private? Are my photos uploaded to any server?', a: 'Your images never leave your device. The entire watermark workflow — loading images, rendering layers, exporting the final file — happens inside your browser using JavaScript and the HTML5 Canvas API. When you upload an image, the File API creates an in-memory representation of the file. When you add a watermark layer, it\'s drawn onto an off-screen canvas element. When you download the result, the canvas\'s pixel data is converted to a Blob and handed to the browser for download — entirely locally. No network request is made that involves your image data. Additionally, the tool includes a page-unload guard: if you attempt to close or navigate away from the page while images are loaded, the browser prompts you to confirm, preventing accidental loss of your work-in-progress session.' },
    ],
    qrCodeGenerator: [
      { q: 'Does this QR code expire?', a: 'No, these QR codes do not expire and guarantee a lifetime support. As they store hardcoded instructions in the form of bits inside square boxes, the information stored in them cannot be changed or removed. You can access the information in code just by scanning it with any camera even after years.' },
      { q: 'Why is my QR code not scanning?', a: 'Yor QR code may fail to be scanned due to the following reasons: i. Low Contrast: If you select the similar background and foreground colors, it affects the visibility of the QR code and cause failure of scanning. But our tool guides you during customization about low contrast or any other scan related issue. ii. Large data: If you generate a QR code for very large text of data, it becomes very complex and causes trouble while scanning. A text length validator guides you about the optimal text length during QR code generation. iii. Blurry print: Make sure to download your QR code in a high-resolution format like PNG or SVG which are already supported by our tool.' },
      { q: 'Can I add logo in the QR code?', a: 'Absolutely, you can add a custom logo or select one from the in-built logo library for you QR code. Your selected logo will be adjusted automatically in the center of QR code.' },
      { q: 'How can I scan the QR code?', a: 'You can scan the QR code using your mobile camera. But for automatic redirection to the link and many more advanced features, you can visit our QR code scanner using the https://photremium.com/qr-code-scanner link for free.' },
      { q: 'How many QR codes can I generate for free?', a: 'UNLIMITED! you can generate multiple QR codes for free. All features, customizations and templates are free forever for all. No need to subscribe for enjoying premium features.' },
      { q: 'Do I need to login/signup to generate QR code?', a: 'No, just enter your text, generate QR code, and download after customization. There is no need to login or signup as all features can be accessed completely free of cost.' },
    ],
    qrCodeScanner: [
      { q: 'Can I scan QR code from saved images and screenshots?', a: '**Yes**, you can scan a QR code from saved images or screenshots. Our QR code scanner https://photremium.com/qr-code-scanner lets you scan the QR code from gallery. You just have to select the image and you will get all the information redeemed from that QR code.' },
      { q: 'Do I need to install the App for QR code scanner?', a: '**No**, our web-based QR code scanner allows you to scan QR code without need for any app installation. You can access our advanced QR code scanner on any device for free.' },
      { q: 'Why is the scanner not recognizing the QR code?', a: 'Your QR code is not being scanned due to any of the following reasons: i. Inadequate light: Scanning the QR code in dim light may cause trouble in scanning. Turn on the flashlight and increase the brightness if you are scanning from any screen. ii. Distance: Scanning QR code from inappropriate distance will also cause issues. The optimal distance to scan the QR code is almost 10 inches (25cm). iii. Dirty camera lenses: QR code may also fail to scan due to dirty lenses. Clean the lenses before scanning it. “You see a box grid in the scanner? Just fit the QR code into it to scan it successfully.”' },
      { q: 'Does this tool scan an inverted or a colorful QR code?', a: '**Yes**, you can scan QR in any color or orientation. However, if background and foreground color contrast is low, it may cause difficulty in scanning.' },
      { q: 'Is this tool free to use?', a: '**Yes**, this tool is totally free to use. You can scan unlimitd QR codes free forever.' },
    ],
    faceBlur: [
      { q: 'How does face detection work?', a: 'We use an AI-powered face detection model that runs directly in your browser. It identifies faces in the image so you can apply a blur effect to them.' },
      { q: 'Can I choose which faces to blur?', a: 'Yes. After detection, each face region is highlighted and you can select which ones to blur and adjust the blur intensity for each.' },
      { q: 'What blur styles are available?', a: 'You can choose from Gaussian blur, pixelation, or a solid color overlay. The intensity of each effect is fully adjustable.' },
      { q: 'Can I blur faces in multiple images at once?', a: 'Yes. Upload several images, configure blur regions, and download all processed images together as a ZIP or individually.' },
      { q: 'Are my photos sent to a remote server?', a: 'No. The AI model runs in your browser using WebAssembly. Your photos are never uploaded to any server.' },
    ],
    photoEditor: [
      { q: 'What editing features are available?', a: 'You can adjust brightness, contrast, saturation, apply filters, add text, overlays, frames, stickers, and much more — all inside your browser.' },
      { q: 'Can I undo and redo changes?', a: 'Yes. The editor supports full undo/redo history so you can experiment freely without worrying about losing your work.' },
      { q: 'What file formats can I edit and export?', a: 'You can edit JPG, PNG, WEBP, and other common formats. The edited image can be exported in your preferred format and quality.' },
      { q: 'Does the editor work on mobile?', a: 'Yes. The editor is fully responsive and touch-friendly, so you can edit images on your phone or tablet.' },
      { q: 'Are my images stored on your servers?', a: 'No. All editing is done locally. Your images never leave your device.' },
    ],
  },
};

export default en;